import React, { useState, useRef } from 'react';
import { useT } from '../../builder/BuilderApp.jsx';
import { PACKAGES, PKG_TEXT } from '../../data/packages.js';

export default function SalesDialog({ pkgId, onClose, prefillEmail, lang, inline }) {
  const [form, setForm] = useState({
    company: '', contact: '', email: prefillEmail || '', phone: '', concept: '', needs: '',
  });
  const [files, setFiles] = useState([]);
  const [state, setState] = useState('idle');
  const [dragOver, setDrag] = useState(false);
  const fileRef = useRef(null);
  const t = useT();

  if (!pkgId) return null;

  const pkg = PACKAGES.find(p => p.id === pkgId);
  const ac  = PKG_TEXT[pkgId] || '#60a5fa';

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const valid = form.company.trim() && form.contact.trim() && form.email.includes('@');

  const submit = async () => {
    if (!valid) return;
    setState('submitting');
    // Mock: no fetch
    await new Promise(r => setTimeout(r, 800));
    setState('done');
  };

  const addFiles = (fileList) => {
    const MAX = 5;
    const allowed = Array.from(fileList).slice(0, MAX - files.length);
    allowed.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setFiles(prev => {
          if (prev.length >= MAX) return prev;
          return [...prev, { name: file.name, size: file.size, dataUrl: ev.target.result }];
        });
      };
      reader.readAsDataURL(file);
    });
  };

  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));
  const fmtSize = (b) => b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

  const fieldStyle = {
    display: 'block', width: '100%', boxSizing: 'border-box',
    background: 'rgba(255,255,255,.05)', border: '1px solid var(--border2)',
    borderRadius: 10, padding: '.6rem .85rem', color: 'var(--text)',
    fontSize: '.85rem', outline: 'none', fontFamily: 'inherit',
  };
  const labelStyle = {
    display: 'block', fontSize: '.72rem', fontWeight: 700, color: 'var(--muted)',
    letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: '.35rem',
  };

  const card = (
    <div style={{
      background: '#0b1120', border: '1px solid var(--border)', borderRadius: 22,
      width: '100%', maxWidth: 520, maxHeight: inline ? undefined : '92vh', overflowY: 'auto',
      boxShadow: inline ? 'none' : '0 32px 80px rgba(0,0,0,.6)',
      animation: 'fadeIn .2s ease',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '1.5rem 1.5rem 0' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.3rem' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: pkg?.color || ac, flexShrink: 0 }}/>
            <span style={{ fontSize: '.7rem', fontWeight: 800, color: ac, textTransform: 'uppercase', letterSpacing: '.07em' }}>{t.sd_pkg_lbl}: {pkg?.label}</span>
          </div>
          <h3 style={{ fontSize: '1.2rem', fontWeight: 800, margin: '0 0 .25rem', letterSpacing: '-.01em' }}>{t.sd_title}</h3>
          <p style={{ fontSize: '.8rem', color: 'var(--muted)', margin: 0, lineHeight: 1.5 }}>{t.sd_sub}</p>
        </div>
        <button onClick={onClose} style={{ background: 'rgba(255,255,255,.07)', border: 'none', color: 'var(--muted)', borderRadius: 8, width: 30, height: 30, fontSize: '1.1rem', cursor: 'pointer', flexShrink: 0, marginLeft: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
      </div>

      <div style={{ padding: '1.25rem 1.5rem 1.5rem' }}>
        {state === 'done' ? (
          <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
            <div style={{ fontSize: '2.75rem', marginBottom: '1rem' }}>🎉</div>
            <h4 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: '.6rem', color: '#34d399' }}>{t.sd_done_h}</h4>
            <p style={{ fontSize: '.83rem', color: 'var(--muted)', lineHeight: 1.65, marginBottom: '1.5rem' }}>{t.sd_done_p(form.email)}</p>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>{t.sd_close}</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.85rem' }}>
              <div>
                <label style={labelStyle}>{t.sd_company}</label>
                <input style={fieldStyle} placeholder="Acme AS" value={form.company} onChange={e => upd('company', e.target.value)}/>
              </div>
              <div>
                <label style={labelStyle}>{t.sd_contact}</label>
                <input style={fieldStyle} placeholder="Ola Nordmann" value={form.contact} onChange={e => upd('contact', e.target.value)}/>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.85rem' }}>
              <div>
                <label style={labelStyle}>{t.sd_email_lbl}</label>
                <input type="email" style={fieldStyle} placeholder="ola@acme.no" value={form.email} onChange={e => upd('email', e.target.value)}/>
              </div>
              <div>
                <label style={labelStyle}>{t.sd_phone}</label>
                <input type="tel" style={fieldStyle} placeholder="+47 000 00 000" value={form.phone} onChange={e => upd('phone', e.target.value)}/>
              </div>
            </div>
            <div>
              <label style={labelStyle}>{t.sd_concept}</label>
              <textarea style={{ ...fieldStyle, resize: 'vertical', minHeight: 72, lineHeight: 1.55 }} placeholder={t.sd_concept_ph} value={form.concept} onChange={e => upd('concept', e.target.value)}/>
            </div>
            <div>
              <label style={labelStyle}>{t.sd_needs}</label>
              <textarea style={{ ...fieldStyle, resize: 'vertical', minHeight: 72, lineHeight: 1.55 }} placeholder={t.sd_needs_ph} value={form.needs} onChange={e => upd('needs', e.target.value)}/>
            </div>
            {/* File upload */}
            <div>
              <label style={labelStyle}>{t.sd_files_lbl}</label>
              <input
                ref={fileRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.svg,.zip,.txt,.csv"
                style={{ display: 'none' }}
                onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
              />
              <div
                onClick={() => files.length < 5 && fileRef.current.click()}
                onDragOver={e => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={e => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
                style={{
                  border: `1px dashed ${dragOver ? ac : 'rgba(255,255,255,.18)'}`,
                  borderRadius: 10, padding: '.85rem 1rem', textAlign: 'center',
                  cursor: files.length < 5 ? 'pointer' : 'default',
                  background: dragOver ? 'rgba(99,102,241,.07)' : 'transparent',
                  transition: 'border-color .15s,background .15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.55rem',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 10V4M5 7l3-3 3 3" stroke={dragOver ? ac : 'var(--muted)'} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  <rect x="1" y="11" width="14" height="4" rx="1.5" fill="none" stroke={dragOver ? ac : 'var(--muted)'} strokeWidth="1.2"/>
                </svg>
                <span style={{ fontSize: '.78rem', color: dragOver ? ac : 'var(--dim)' }}>{t.sd_files_hint}</span>
              </div>
              {files.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem', marginTop: '.6rem' }}>
                  {files.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '.6rem', background: 'rgba(255,255,255,.04)', border: '1px solid var(--border)', borderRadius: 8, padding: '.45rem .75rem' }}>
                      <span style={{ fontSize: '.8rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text)' }}>{f.name}</span>
                      <span style={{ fontSize: '.7rem', color: 'var(--dim)', flexShrink: 0 }}>{fmtSize(f.size)}</span>
                      <button onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: '0 .1rem', fontSize: '.78rem', lineHeight: 1, flexShrink: 0 }}>{t.sd_remove} ×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Submit */}
            <button
              className="btn btn-primary"
              onClick={submit}
              disabled={!valid || state === 'submitting'}
              style={{ marginTop: '.25rem', paddingTop: '.75rem', paddingBottom: '.75rem', opacity: valid ? 1 : .45 }}
            >
              {state === 'submitting'
                ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.5rem' }}>
                    <svg style={{ animation: 'spin 1s linear infinite' }} width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="7" cy="7" r="5.5" stroke="rgba(255,255,255,.2)" strokeWidth="1.5"/>
                      <path d="M7 1.5a5.5 5.5 0 015.5 5.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    {t.sd_sending}
                  </span>
                )
                : t.sd_send
              }
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (inline) return card;
  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,.72)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.25rem' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {card}
    </div>
  );
}
